import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { pitchmanApi } from "../api/pitchman";
import { trackEvent } from "../lib/analytics";
import { PITCHMAN_LAUNCHED } from "../lib/launch";

// PitchMan, the free pitch shifter: one page for both versions, the browser
// extension (repitch YouTube to sample from it) and the AU/VST3 plugin (the
// same fader as a DAW insert). Each version's card carries its own download
// buttons, no email asked. The links come from the backend config
// (GET /api/pitchman/links); one not out yet shows as "coming soon".
//
// Rendered inside SiteLayout, so it inherits the top bar, the footer and the
// .tm-landing tokens. Its own styles are the .pm-* rules in App.css.
//
// Images are flat files (/pitchman-*.png), never a public/pitchman/ folder:
// nginx would match that directory before the SPA fallback and /pitchman
// would answer 403. nginx caches them for a week, so a changed image gets a
// new name (-v2, -v3...) or returning visitors keep seeing the old one.

// `downloads` are the card's buttons: `link` is the key in the links
// response, `store` opens a store listing in a new tab (the plugin
// installers download in place).
const WAYS = [
  {
    key: "extension",
    img: { webp: "/pitchman-extension-v3.webp", fallback: "/pitchman-extension-v3.jpg", wide: true },
    items: 4,
    downloads: [
      { link: "extension_url", label: "chrome", store: true },
      { link: "firefox_url", label: "firefox", store: true },
    ],
  },
  {
    key: "plugin",
    img: { webp: "/pitchman-plugin-daw-v3.webp", fallback: "/pitchman-plugin-daw-v3.jpg", wide: true },
    items: 4,
    downloads: [
      { link: "mac_url", label: "mac" },
      { link: "windows_url", label: "windows" },
    ],
  },
];

export default function Pitchman() {
  const { t } = useTranslation("landing");
  // null while loading; a failed call leaves every download "coming soon".
  const [links, setLinks] = useState(null);

  useEffect(() => {
    let alive = true;
    pitchmanApi.links()
      .then(({ data }) => { if (alive) setLinks(data || {}); })
      .catch(() => { if (alive) setLinks({}); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const previous = document.title;
    document.title = t("pitchman.metaTitle");
    return () => { document.title = previous; };
  }, [t]);

  // Unlisted until launch (lib/launch.js): keep it out of search results.
  useEffect(() => {
    if (PITCHMAN_LAUNCHED) return undefined;
    // index.html already carries the site-wide robots tag: rewrite it here and
    // put it back on the way out, rather than adding a second, conflicting one.
    let meta = document.head.querySelector('meta[name="robots"]');
    const created = !meta;
    if (created) {
      meta = document.createElement("meta");
      meta.name = "robots";
      document.head.appendChild(meta);
    }
    const previous = meta.content;
    meta.content = "noindex, nofollow";
    return () => { if (created) meta.remove(); else meta.content = previous; };
  }, []);

  const faqs = [1, 2, 3, 4].map((n) => ({ q: t(`pitchman.faq.q${n}`), a: t(`pitchman.faq.a${n}`) }));

  return (
    <main className="pm">
      <header className="pm-hero">
        <div className="pm-hero-copy">
          <span className="eyebrow"><b>●</b> {t("pitchman.eyebrow")}</span>
          <h1 className="hero-h1">{t("pitchman.title1")}<br /><em>{t("pitchman.titleEm")}</em></h1>
          <p className="pm-lead">{t("pitchman.lead")}</p>
          <div className="pm-ctas">
            <a href="#ways" className="btn btn-primary big-btn">{t("pitchman.ctaPrimary")}</a>
          </div>
          <div className="pm-meta">
            {["range", "where", "price"].map((k) => (
              <div key={k}>{t(`pitchman.meta.${k}Label`)}<b>{t(`pitchman.meta.${k}`)}</b></div>
            ))}
          </div>
        </div>
        <picture className="pm-hero-art">
          <source srcSet="/pitchman-hardware.webp" type="image/webp" />
          <img src="/pitchman-hardware.png" alt={t("pitchman.imageAlt")} width="640" height="1137" />
        </picture>
      </header>

      <section id="ways">
        <div className="section-intro">
          <h2 className="h2">{t("pitchman.ways.title1")}<br />{t("pitchman.ways.title2")}</h2>
          <p className="sub">{t("pitchman.ways.sub")}</p>
        </div>
        <div className="pm-ways">
          {WAYS.map((w) => (
            <article key={w.key} className="pm-way">
              <div className={`pm-way-shot${w.img.wide ? " is-wide" : ""}`}>
                <picture>
                  {w.img.webp && <source srcSet={w.img.webp} type="image/webp" />}
                  <img src={w.img.fallback} alt={t(`pitchman.ways.${w.key}.imageAlt`)} loading="lazy" />
                </picture>
              </div>
              <span className="pm-way-label">{t(`pitchman.ways.${w.key}.label`)}</span>
              <h3>{t(`pitchman.ways.${w.key}.title`)}</h3>
              <ul>
                {Array.from({ length: w.items }, (_, i) => (
                  <li key={i}>{t(`pitchman.ways.${w.key}.item${i + 1}`)}</li>
                ))}
              </ul>
              <p className="pm-way-formats">{t(`pitchman.ways.${w.key}.formats`)}</p>
              <div className="pm-way-actions">
                {w.downloads.map((d) => {
                  const url = links?.[d.link];
                  if (!links || !url) {
                    return (
                      <span key={d.link} className="btn big-btn pm-way-btn is-off" aria-disabled="true">
                        {t(links ? `pitchman.download.${d.label}Soon` : `pitchman.download.${d.label}`)}
                      </span>
                    );
                  }
                  return (
                    <a
                      key={d.link}
                      href={url}
                      className="btn btn-primary big-btn pm-way-btn"
                      {...(d.store ? { target: "_blank", rel: "noreferrer" } : {})}
                      onClick={() => trackEvent("pitchman_download", { target: d.label })}
                    >
                      {t(`pitchman.download.${d.label}`)}
                    </a>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="pm-faq">
        <div className="section-intro">
          <h2 className="h2">{t("pitchman.faq.title")}</h2>
        </div>
        <div className="pm-faq-list">
          {faqs.map((f, i) => (
            <div key={i} className="feat">
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
